// A journal's posts in Plan (the Mac's PostPlan.swift and PostBoard.swift share the rules). On the web the page's item is
// the URL's node, so a post's plan is simply Plan on a post (or one of its sections): its header, then its sections. At
// the journal level a post is a card among the posts, never opened as a folder.
import { JournalPosts, journalPostsInTree, postContaining, type JournalPostRef, type MetadataValue, type Project, type TreeNode } from '@writing-suite/core';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { followLink } from '../../shell/route.ts';
import { structureEdits, useWorkspace } from '../../shell/workspace.ts';
import { CommitTextArea, FieldEditor } from '../FieldEditor.tsx';
import { formatNumber, pathTo } from '../helpers.ts';

/** A journal's posts arrangement: where posts and sections apply. */
export const isJournalPosts = (project: Project, arrangement: { role: string }) => project.type === 'journal' && arrangement.role === 'manuscript';

/** The post whose plan Plan shows: the node itself when it's a post, or the post holding it. */
export function plannedPost(tree: TreeNode[], nodeId: string | null): { post: JournalPostRef; node: TreeNode } | null {
  if (!nodeId) return null;
  const post = postContaining(nodeId, journalPostsInTree(tree));
  const node = post ? pathTo(tree, post.nodeId)?.at(-1) ?? null : null;
  return post && node ? { post, node } : null;
}

/** `withoutSections`: the journal's posts and their folders, each post's sections left out. */
export function withoutSections(tree: TreeNode[]): TreeNode[] {
  const posts = new Set(journalPostsInTree(tree).map((p) => p.nodeId));
  const walk = (nodes: TreeNode[]): TreeNode[] => nodes.map((n) => ({ ...n, children: posts.has(n.id) ? [] : walk(n.children) }));
  return walk(tree);
}

/** "#141 Coming home", or the title alone before the post has a number. */
export function postName(project: Project, post: JournalPostRef, title: string): string {
  const number = JournalPosts.number(project.fragment(post.fragmentId)?.metadata);
  return number !== null ? `#${number} ${title || 'Untitled'}` : title || 'Untitled';
}

/** The post's header above its sections (`PostPlanHeader`): the way back, its name, Write and New Section, what it's for,
 *  Status and Publish date, its words (against its target). */
export function PostHeader({ project, tree, hrefFor, live, page, post, node }: Pick<PageProps, 'project' | 'tree' | 'hrefFor' | 'live' | 'page'> & { post: JournalPostRef; node: TreeNode }) {
  const ws = useWorkspace();
  const write = !!ws?.canWrite && !!live;
  const fragment = project.fragment(post.fragmentId);
  const fields = project.fields();
  const entities = project.entities();
  const parent = post.parentId ? pathTo(tree, post.parentId)?.at(-1) ?? null : null;
  const here = `${page.room}/${page.page}`;
  const back = hrefFor(here, parent?.id ?? null);
  const writeHref = hrefFor('write', post.nodeId);
  const words = project.subtreeWords(node);
  const target = fragment?.metadata['word-target']?.type === 'number' ? fragment.metadata['word-target'].value : null;
  const field = (id: string) => fields.find((f) => f.id === id);
  const set = (id: string) => (value: MetadataValue | null) => live?.edit((e) => e.setMetadata(post.fragmentId, id, value));
  const newSection = () => {
    if (!ws) return;
    const made = structureEdits(ws).newSection(post.nodeId);
    if (made) ws.setRenaming(made, 'board');
  };
  return (
    <header className="post-plan-head" data-post-plan={post.nodeId}>
      <p className="post-plan-back">
        <a href={back} onClick={(e) => followLink(e, back)} title="Back to the journal’s posts">
          ‹ {parent ? parent.title || 'Untitled' : (ws?.arrangement.name ?? 'Posts')}
        </a>
        <span aria-hidden="true"> · </span>
        <span>Plan</span>
      </p>
      <div className="post-plan-title">
        <h1 tabIndex={-1}>{postName(project, post, node.title)}</h1>
        <span className="page-actions">
          <a className="quiet-button" href={writeHref} onClick={(e) => followLink(e, writeHref)} title="Open the post and its sections in Write">
            Write
          </a>
          {write ? (
            <button type="button" className="quiet-button" onClick={newSection}>
              New section
            </button>
          ) : null}
        </span>
      </div>
      <CommitTextArea
        id="post-plan-synopsis"
        className="post-plan-synopsis"
        value={fragment?.synopsis ?? ''}
        readOnly={!write}
        rows={2}
        placeholder="What this post is for"
        aria-label="What this post is for"
        onCommit={(t) => live?.edit((e) => e.setSynopsis(post.fragmentId, t))}
      />
      <div className="post-plan-fields">
        {(['status', 'publish-date'] as const).map((id) => {
          const f = field(id);
          if (!f) return null;
          return (
            <span className="post-plan-field" key={id} data-field-id={id}>
              <span className="field-label">{f.name}</span>
              <FieldEditor field={f} value={fragment?.metadata[id]} project={project} entities={entities} readOnly={!write} inline onChange={set(id)} />
            </span>
          );
        })}
        <span className="post-plan-words" data-post-words={words}>
          {target ? `${formatNumber(words)} of ${formatNumber(target)} words` : words === 1 ? '1 word' : `${formatNumber(words)} words`}
        </span>
      </div>
    </header>
  );
}

/** The post's own words, before its first section: first on the board, not moved like the sections' cards. */
export function PostOpeningCard({ project, post, hrefFor }: Pick<PageProps, 'project' | 'hrefFor'> & { post: JournalPostRef }) {
  const href = hrefFor('write', post.nodeId);
  const words = project.words(post.fragmentId);
  return (
    <li className="card post-opening" data-post-opening>
      <a href={href} draggable={false} onClick={(e) => followLink(e, href)}>
        <span className="card-title">
          <span className="card-title-text">Opening text</span>
        </span>
        <span className="card-synopsis none">The post’s own words, before its first section.</span>
        <span className="card-foot">
          <span />
          <span className="card-words">{words === 1 ? '1 word' : `${formatNumber(words)} words`}</span>
        </span>
      </a>
    </li>
  );
}

/** A post with no sections yet. */
export function PostPlanEmpty({ post }: { post: JournalPostRef }) {
  const ws = useWorkspace();
  return (
    <div className="post-plan-empty" data-post-empty>
      <p className="empty-note">Plan this post in sections. Each section is a card here, in the order the post reads; Write shows them as one text.</p>
      {ws?.canStructure ? (
        <button
          type="button"
          className="quiet-button"
          onClick={() => {
            const made = structureEdits(ws).newSection(post.nodeId);
            if (made) ws.setRenaming(made, 'board');
          }}
        >
          New section
        </button>
      ) : null}
    </div>
  );
}
